// Icon + color pools for enroll-site config, shared by the public landing page
// (renders them) and the internal builder (selects from them), so the picker
// always matches what members see.
import {
  Activity,
  CalendarDays,
  BookOpen,
  Eye,
  FileText,
  Globe,
  FlaskConical,
  HardHat,
  HeartPulse,
  Hospital,
  Phone,
  Pill,
  PlayCircle,
  ScrollText,
  Search,
  PiggyBank,
  ShieldCheck,
  Smile,
  HeartHandshake,
  Stethoscope,
  Umbrella,
  Video,
  type LucideIcon,
} from 'lucide-react';
import type {CoverageIcon, LandingResource} from '@client/pages/public/enroll/types';

// Generic glyph pool for coverage cards — cosmetic, chosen independently of color.
export const COVERAGE_ICON_POOL: Record<CoverageIcon, {Icon: LucideIcon; label: string}> = {
  stethoscope: {Icon: Stethoscope, label: 'Stethoscope'},
  'shield-check': {Icon: ShieldCheck, label: 'Shield'},
  'heart-pulse': {Icon: HeartPulse, label: 'Heartbeat'},
  eye: {Icon: Eye, label: 'Eye'},
  pill: {Icon: Pill, label: 'Pill'},
  'hard-hat': {Icon: HardHat, label: 'Hard hat'},
  activity: {Icon: Activity, label: 'Activity'},
  smile: {Icon: Smile, label: 'Smile'},
  umbrella: {Icon: Umbrella, label: 'Umbrella'},
  'heart-handshake': {Icon: HeartHandshake, label: 'Care'},
  video: {Icon: Video, label: 'Video'},
  'flask-conical': {Icon: FlaskConical, label: 'Lab'},
  hospital: {Icon: Hospital, label: 'Hospital'},
  phone: {Icon: Phone, label: 'Phone'},
  'piggy-bank': {Icon: PiggyBank, label: 'Savings'},
};

// Curated accent-color pool (readable on white). Values are what gets stored.
export const COVERAGE_COLOR_POOL: {value: string; label: string}[] = [
  {value: '#16a34a', label: 'Green'},
  {value: '#2563eb', label: 'Blue'},
  {value: '#0d9488', label: 'Teal'},
  {value: '#7c3aed', label: 'Purple'},
  {value: '#db2777', label: 'Pink'},
  {value: '#b45309', label: 'Amber'},
  {value: '#dc2626', label: 'Red'},
  {value: '#4f46e5', label: 'Indigo'},
  {value: '#ea580c', label: 'Orange'},
  {value: '#475569', label: 'Slate'},
];

// Accent-color pool for plan cards — broader and more distinct than the coverage-card
// pool. Includes the metal-tier tones (bronze/silver/gold) and the lighter accents that
// live plans actually use, plus a few in the same veins (green/indigo/red) to round it out.
export const PLAN_ACCENT_COLOR_POOL: {value: string; label: string}[] = [
  {value: '#2563eb', label: 'Blue'},
  {value: '#38bdf8', label: 'Sky'},
  {value: '#0891b2', label: 'Cyan'},
  {value: '#0d9488', label: 'Teal'},
  {value: '#16a34a', label: 'Green'},
  {value: '#4f46e5', label: 'Indigo'},
  {value: '#7c3aed', label: 'Purple'},
  {value: '#a78bfa', label: 'Violet'},
  {value: '#db2777', label: 'Pink'},
  {value: '#dc2626', label: 'Red'},
  {value: '#ea580c', label: 'Orange'},
  {value: '#b45309', label: 'Bronze'},
  {value: '#d4af37', label: 'Gold'},
  {value: '#94a3b8', label: 'Silver'},
];

export const RESOURCE_ICONS: Record<LandingResource['icon'], {Icon: LucideIcon; label: string}> = {
  resources: {Icon: BookOpen, label: 'Resources'},
  guide: {Icon: FileText, label: 'Guide'},
  notices: {Icon: ScrollText, label: 'Notices'},
  search: {Icon: Search, label: 'Search'},
  pharmacy: {Icon: Pill, label: 'Pharmacy'},
  video: {Icon: PlayCircle, label: 'Video'},
  calendar: {Icon: CalendarDays, label: 'Calendar'},
  website: {Icon: Globe, label: 'Website'},
};
